// Site detail (WP 0.5): ERP-owned fields read-only with a lock, plus cameras, devices, contacts,
// site maps and ingest addresses.

import { useState, type ReactNode } from 'react';
import { api, type AlarmEndpoints, type Camera, type Contact, type ContractedHours, type Device, type Json, type JsonObject, type SiteDetail } from '../api.ts';
import { byCompanyRole, isCompanyContact, isUsable, rolesAt } from '../components/ContactOptions.tsx';
import { Icon } from '../components/Icon.tsx';
import { SitemapView } from '../components/SitemapView.tsx';
import { Dot, Empty, ErpLink, ErrorBox, Loading, Pill, RoField, SourceBadge, StatusPill } from '../components/ui.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { href, navigate } from '../router.ts';
import { useSession } from '../session.tsx';
import { useRefreshOn, useStreamEvent } from '../stream.tsx';
import { fmtDateTime, fmtLocal, fmtTime, OPERATOR_TZ, relAge, tzAbbr } from '../time.ts';
import { ReadOnlyBanner } from './MasterNav.tsx';

type Tab = 'overview' | 'cameras' | 'devices' | 'contacts' | 'maps' | 'ingest';
const TABS: readonly Tab[] = ['overview', 'cameras', 'devices', 'contacts', 'maps', 'ingest'];

/* ---------- small JSON helpers for payload objects ---------- */

function isObj(v: Json | undefined): v is JsonObject {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function str(o: JsonObject | null | undefined, k: string): string | null {
  const v = o?.[k];
  return v === undefined || v === null ? null : typeof v === 'object' ? JSON.stringify(v) : String(v);
}
function obj(o: JsonObject | null | undefined, k: string): JsonObject | null {
  const v = o?.[k];
  return isObj(v) ? v : null;
}
function arr(o: JsonObject | null | undefined, k: string): JsonObject[] {
  const v = o?.[k];
  return Array.isArray(v) ? v.filter(isObj) : [];
}

/* ---------- formatting ---------- */

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function daysText(days: string[]): string {
  const idx = days.map((d) => DAY_ORDER.indexOf(d.toLowerCase())).filter((i) => i >= 0).sort((a, b) => a - b);
  if (idx.length === 7) return 'Every day';
  const contiguous = idx.length > 2 && idx.every((v, i) => i === 0 || v === (idx[i - 1] ?? -2) + 1);
  if (contiguous) return `${cap(DAY_ORDER[idx[0] ?? 0] ?? '')}–${cap(DAY_ORDER[idx[idx.length - 1] ?? 0] ?? '')}`;
  return idx.map((i) => cap(DAY_ORDER[i] ?? '')).join(', ');
}

function hoursLines(h: ContractedHours | null): string[] {
  if (!h) return ['Not set'];
  const out: string[] = [];
  if (h.mode === 'always') out.push('24/7');
  else if (h.mode === 'never') out.push('Not monitored');
  else if (h.weekly?.length) for (const w of h.weekly) out.push(`${daysText(w.days)} ${w.start}–${w.end}`);
  else out.push(h.mode);
  for (const e of h.exceptions ?? []) {
    const win = e.windows.length ? ` ${e.windows.map((w) => `${w.start}–${w.end}`).join(', ')}` : '';
    out.push(`${e.date}: ${e.mode}${win}${e.label ? ` (${e.label})` : ''}`);
  }
  if (h.effective_from || h.effective_to) out.push(`Effective ${h.effective_from ?? '…'} to ${h.effective_to ?? 'open'}`);
  return out;
}


/* ---------- tabs ---------- */

/** Contract 1.5: the site's alarms are paused in the ERP (or the pause has passed its until but is still set there). */
function PausedBanner({ s }: { s: SiteDetail }) {
  const p = s.alarmsPaused;
  if (!p) return null;
  const ended = !s.alarmsPausedNow;
  return (
    <div className="banner paused" role="status">
      <Icon name="pause" />
      <span>
        <b>{ended ? 'Alarm pause ended' : 'Alarms paused'}</b> since {fmtDateTime(p.since, s.timeZone)} {tzAbbr(s.timeZone, Date.parse(p.since))}
        {p.until && <> · {ended ? 'ended' : 'until'} {fmtDateTime(p.until, s.timeZone)}</>}
        {p.note && <> — {p.note}</>}
        {ended ? '. Its end time has passed but the ERP still has it set: resume it on the site’s SOC card in the ERP.' : '. Alarms are kept but raise no events, except panic, duress and fire. Set and cleared in the ERP.'}
      </span>
      <span className="sp" />
      <a className="btn sm" href={href.alarms(s.id, 'received_paused')}>
        Received while paused
      </a>
    </div>
  );
}

function Overview({ s }: { s: SiteDetail }) {
  const now = useNow(1000);
  const a = s.address;
  return (
    <>
      <PausedBanner s={s} />
      <ReadOnlyBanner />
      <div className="fgrid">
        <RoField label="Company">
          <a href={href.sites(s.companyId)}>{s.companyName}</a>
        </RoField>
        <RoField label="Site name">{s.name}</RoField>
        <RoField label="Site code">{s.siteCode && <span className="mono">{s.siteCode}</span>}</RoField>
        <RoField label="ERP site ID">
          <span className="mono">{s.id}</span>
        </RoField>
        <RoField label="Status">
          <StatusPill status={s.status} />
        </RoField>
        <RoField label="Vertical">{s.vertical}</RoField>
        <RoField label="Address" wide>
          {a ? (
            <span className="addr">
              {[a.line1, a.line2].filter(Boolean).join(', ')}
              <br />
              {[a.city, a.region, a.postal_code].filter(Boolean).join(' ')} {a.country}
            </span>
          ) : null}
        </RoField>
        <RoField label="Cross streets">{s.crossStreets}</RoField>
        <RoField label="Geo">{s.geo ? <span className="mono">{s.geo.lat.toFixed(5)}, {s.geo.lon.toFixed(5)}</span> : null}</RoField>
        <RoField label="Time zone">
          <span className="mono">
            {s.timeZone} · {fmtTime(now, s.timeZone)} {tzAbbr(s.timeZone, now)}
          </span>
        </RoField>
        <RoField label="Service plan" wide>
          {s.servicePlan ? (
            <span>
              {s.servicePlan.name} <span className="mono dim">({s.servicePlan.code})</span>
              <span className="chips mt">
                {s.servicePlan.features.map((f) => (
                  <span key={f} className="chip">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </span>
            </span>
          ) : null}
        </RoField>
        <RoField label="Contracted hours (site time)" wide>
          <span className="lines">
            {hoursLines(s.contractedHours).map((l) => (
              <span key={l}>{l}</span>
            ))}
          </span>
        </RoField>
        <RoField label="In contracted hours now">
          <span>
            <Dot on={s.inContractedHoursNow} /> {s.inContractedHoursNow === null ? 'Unknown' : s.inContractedHoursNow ? 'Yes' : 'No'}
          </span>
        </RoField>
        <RoField label="Equipment">
          {s.cameras.length} cameras · {s.devices.length} devices
        </RoField>
      </div>
      <div className="lbl">Replica record</div>
      <div className="fgrid">
        <RoField label="Source">
          <SourceBadge source={s.source} />
        </RoField>
        <RoField label="Version">
          <span className="mono">v{s.version}</span>
        </RoField>
        <RoField label="Applied">
          <span className="mono">{fmtLocal(s.appliedAt)}</span>
        </RoField>
        <RoField label="Active">{s.active ? 'Yes' : 'No (soft-deleted)'}</RoField>
        <RoField label="Held change">{s.held ? <Pill tone="warn">Waiting for a supervisor</Pill> : 'None'}</RoField>
      </div>
    </>
  );
}

/** Copies a value; the label says when it worked. */
function CopyButton({ value, what }: { value: string; what: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn sm ghost"
      title={`Copy ${what}`}
      aria-label={`Copy ${what}`}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? 'copied' : 'copy'}
    </button>
  );
}

/**
 * Contract 1.4 alarm endpoints (4.7): the address and URL a device or NX rule sends this camera's (or site's) alarms
 * to, and when one last arrived. Null endpoints: not visible to this role, or the site has no alarm key / tag yet.
 */
function Endpoints({ e, tag, last }: { e: AlarmEndpoints | null; tag?: string | null; last: string | null }) {
  if (!e) {
    if (tag === undefined) return <span className="dim">—</span>;
    return tag ? <span className="mono dim">tag {tag}</span> : <span className="warn-t">no asset tag</span>;
  }
  return (
    <div className="endpoints">
      {tag && <div className="mono dim">tag {tag}</div>}
      <div className="ep">
        <span className="mono">{e.email}</span> <CopyButton value={e.email} what="email address" />
      </div>
      <div className="ep">
        <span className="mono">{e.url}</span> <CopyButton value={e.url} what="HTTPS URL" />
      </div>
      <div className={last ? 'note' : 'note warn-t'}>{last ? `last alarm ${relAge(last)}` : 'no alarm received yet'}</div>
    </div>
  );
}

/** Records the ERP deleted: kept as history, not monitored, not offered anywhere. Collapsed under the tab's list. */
function DeletedInErp({ what, items }: { what: string; items: { id: string; name: string; note: string | null }[] }) {
  if (!items.length) return null;
  return (
    <details className="deleted-records">
      <summary>
        Deleted in the ERP ({items.length}) <span className="note">kept as history; not monitored or offered for setup</span>
      </summary>
      <ul className="deleted-list" aria-label={`Deleted ${what}`}>
        {items.map((x) => (
          <li key={x.id}>
            <span className="strong">{x.name}</span> <span className="mono note">{x.id}</span>
            {x.note && <span className="note"> · {x.note}</span>} <Pill tone="neutral">deleted</Pill>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CamerasTab({ s }: { s: SiteDetail }) {
  if (!s.cameras.length) return <Empty>No cameras for this site.</Empty>;
  const cams = s.cameras.slice().sort((a, b) => (a.displayNumber ?? 9999) - (b.displayNumber ?? 9999) || a.name.localeCompare(b.name));
  return (
    <div className="tw">
      <table>
        <thead>
          <tr>
            <th scope="col" className="num">#</th>
            <th scope="col">Camera</th>
            <th scope="col">Status</th>
            <th scope="col">Health</th>
            <th scope="col">NX system / camera</th>
            <th scope="col">Alarm endpoints</th>
            <th scope="col">Capabilities</th>
            <th scope="col">Source</th>
            <th scope="col">
              <span className="sr-only">ERP</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {cams.map((c) => (
            <CameraRow key={c.id} c={c} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CameraRow({ c }: { c: Camera }) {
  const caps = c.capabilities;
  const capList = caps
    ? [caps.ptz && 'PTZ', caps.two_way_audio && 'Two-way audio', caps.ir && 'IR', caps.thermal && 'Thermal', caps.outputs ? `Outputs${typeof caps.outputs === 'number' ? ` ×${caps.outputs}` : ''}` : null].filter(
        (x): x is string => typeof x === 'string',
      )
    : [];
  return (
    <tr className={c.active ? '' : 'inactive'}>
      <td className="mono num">{c.displayNumber ?? '—'}</td>
      <td>
        <div className="strong">{c.name}</div>
        <div className="note">
          {[c.viewType, c.locationDescription].filter(Boolean).join(' · ') || '—'}
        </div>
        <div className="note mono">{c.id}</div>
      </td>
      <td>
        <StatusPill status={c.status} />
        {c.held && <Pill tone="warn">held</Pill>}
      </td>
      <td>
        <StatusPill
          status={c.health?.status ?? 'unknown'}
          title={c.health ? `Last seen ${fmtLocal(c.health.lastSeenAt)} · changed ${fmtLocal(c.health.changedAt)}` : 'No health data yet'}
        />
        {c.health?.lastSeenAt && <div className="note">{relAge(c.health.lastSeenAt)}</div>}
      </td>
      <td className="mono small">
        <div title="NX system ID">{c.nxSystemId ?? <span className="warn-t">no system</span>}</div>
        <div title="NX camera ID">{c.nxCameraId ?? <span className="warn-t">no camera</span>}</div>
      </td>
      <td className="small">
        <Endpoints e={c.alarmEndpoints ?? null} tag={c.assetTag ?? null} last={c.lastEndpointAlarmAt ?? null} />
        {c.analyticsBindings.map((b, i) => (
          <div key={i} className="mono dim" title="Analytics binding (deprecated in contract 1.4)">
            binding {b.engine} · {b.analytics_device_id}
            {b.channel !== null && b.channel !== undefined ? ` · ch ${b.channel}` : ''}
          </div>
        ))}
      </td>
      <td>
        <span className="chips">
          {capList.length ? capList.map((x) => <span key={x} className="chip">{x}</span>) : <span className="dim">—</span>}
        </span>
      </td>
      <td>
        <SourceBadge source={c.source} />
        <div className="note mono">v{c.version}</div>
      </td>
      <td>
        <ErpLink url={c.erpUrl} compact label="ERP" />
      </td>
    </tr>
  );
}

function KV({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="kvl">
      <span className="k">{k}</span>
      <span className="v">{children}</span>
    </div>
  );
}

function DeviceDetail({ d, devById, camById }: { d: Device; devById: Map<string, Device>; camById: Map<string, Camera> }) {
  const det = d.detail;
  const control = obj(det, 'control');
  const items: ReactNode[] = [];
  if (d.kind === 'speaker') {
    if (str(det, 'zone_name')) items.push(<KV key="z" k="Zone">{str(det, 'zone_name')}</KV>);
  }
  if (d.kind === 'relay') {
    if (str(det, 'function')) items.push(<KV key="f" k="Function">{str(det, 'function')}</KV>);
    if (str(det, 'hardware_max_on_seconds')) items.push(<KV key="m" k="Max on">{str(det, 'hardware_max_on_seconds')} s</KV>);
  }
  if (control) {
    const method = str(control, 'method');
    const ioId = str(control, 'io_module_id') ?? d.ioModuleId;
    const ch = str(control, 'channel') ?? (d.channel !== null ? String(d.channel) : null);
    items.push(
      <KV key="c" k="Control">
        <span className="mono">{method ?? '—'}</span>
        {str(control, 'api_profile') && <span className="mono dim"> · {str(control, 'api_profile')}</span>}
        {ioId && (
          <span>
            {' '}
            via {devById.get(ioId)?.name ?? <span className="mono">{ioId}</span>}
            {ch ? ` ch ${ch}` : ''}
          </span>
        )}
        {str(control, 'nx_camera_id') && <span className="mono dim"> · NX {str(control, 'nx_camera_id')}</span>}
      </KV>,
    );
  }
  if (d.kind === 'io_module') {
    if (str(det, 'api_profile')) items.push(<KV key="p" k="API profile"><span className="mono">{str(det, 'api_profile')}</span></KV>);
    if (str(det, 'nx_device_id')) items.push(<KV key="n" k="NX device"><span className="mono">{str(det, 'nx_device_id')}</span></KV>);
    const outs = arr(det, 'outputs');
    const ins = arr(det, 'inputs');
    if (outs.length)
      items.push(
        <KV key="o" k="Outputs">
          {outs.map((o) => `${str(o, 'channel')}: ${str(o, 'name') ?? ''}`).join(' · ')}
        </KV>,
      );
    if (ins.length)
      items.push(
        <KV key="i" k="Inputs">
          {ins.map((o) => `${str(o, 'channel')}: ${str(o, 'name') ?? ''}${str(o, 'normal_state') ? ` (normally ${str(o, 'normal_state')})` : ''}`).join(' · ')}
        </KV>,
      );
  }
  if (d.hostCameraId) items.push(<KV key="h" k="Host camera">{camById.get(d.hostCameraId)?.name ?? <span className="mono">{d.hostCameraId}</span>}</KV>);
  if (!control && d.ioModuleId && d.kind !== 'io_module')
    items.push(
      <KV key="io" k="I/O module">
        {devById.get(d.ioModuleId)?.name ?? d.ioModuleId}
        {d.channel !== null ? ` ch ${d.channel}` : ''}
      </KV>,
    );
  if (!items.length) return <span className="dim">—</span>;
  return <div className="kvls">{items}</div>;
}

const KIND_LABEL: Record<string, string> = { speaker: 'Speaker', relay: 'Relay', io_module: 'I/O module' };

function DevicesTab({ s }: { s: SiteDetail }) {
  if (!s.devices.length) return <Empty>No speakers, relays, or I/O modules for this site.</Empty>;
  const devById = new Map(s.devices.map((d) => [d.id, d]));
  const camById = new Map(s.cameras.map((c) => [c.id, c]));
  const order: Record<string, number> = { speaker: 0, relay: 1, io_module: 2 };
  const devs = s.devices.slice().sort((a, b) => (order[a.kind] ?? 9) - (order[b.kind] ?? 9) || a.name.localeCompare(b.name));
  return (
    <div className="tw">
      <table>
        <thead>
          <tr>
            <th scope="col">Kind</th>
            <th scope="col">Device</th>
            <th scope="col">Status</th>
            <th scope="col">Control detail</th>
            <th scope="col">Source</th>
            <th scope="col">
              <span className="sr-only">ERP</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {devs.map((d) => (
            <tr key={d.id} className={d.active ? '' : 'inactive'}>
              <td>
                <span className={`typ k-${d.kind}`}>
                  <Icon name={d.kind === 'speaker' ? 'speaker' : d.kind === 'relay' ? 'bolt' : 'gear'} />
                  {KIND_LABEL[d.kind] ?? d.kind}
                </span>
              </td>
              <td>
                <div className="strong">{d.name}</div>
                <div className="note">{d.locationDescription ?? '—'}</div>
                <div className="note mono">{d.id}</div>
              </td>
              <td>
                <StatusPill status={d.status} />
              </td>
              <td className="small">
                <DeviceDetail d={d} devById={devById} camById={camById} />
              </td>
              <td>
                <SourceBadge source={d.source} />
                <div className="note mono">v{d.version}</div>
              </td>
              <td>
                <ErpLink url={d.erpUrl} compact label="ERP" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactCard({ c, siteId }: { c: Contact; siteId: string }) {
  const roles = rolesAt(c, siteId);
  const phones = c.phones.slice().sort((a, b) => Number(b.is_primary) - Number(a.is_primary));
  return (
    <div className={`contact-card${isUsable(c) ? '' : ' inactive'}`} title={isUsable(c) ? undefined : 'Inactive in the ERP data: not offered for call procedures or notifications'}>
      <div className="cc-h">
        <div>
          <div className="strong">
            <Icon name="lock" className="dim" title="ERP-owned" /> {c.displayName}
          </div>
          <div className="note">{[c.jobTitle, c.contactType.replace(/_/g, ' ')].filter(Boolean).join(' · ')}</div>
        </div>
        <span className="sp" />
        <StatusPill status={c.status} />
        <ErpLink url={c.erpUrl} compact label="ERP" />
      </div>
      {roles.length > 0 && (
        <div className="chips">
          {roles.map((r) => (
            <span key={r} className="chip">
              {r.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
      <div className="phones">
        {phones.length === 0 && <span className="note">No phone numbers</span>}
        {phones.map((p) => (
          <div key={p.phone_id} className="phone">
            <Icon name="phone" />
            <span className="mono">
              {p.e164}
              {p.extension ? ` x${p.extension}` : ''}
            </span>
            <span className="typ">{p.type}</span>
            {p.is_primary && (
              <span className="primary" title="Primary number">
                ★ primary
              </span>
            )}
          </div>
        ))}
        {c.emails.map((e) => (
          <div key={e.email_id} className="phone">
            <Icon name="mail" />
            <span className="mono">{e.address}</span>
            {e.is_primary && <span className="primary">★ primary</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactsTab({ s }: { s: SiteDetail }) {
  const own = s.contacts.filter((c) => !isCompanyContact(c));
  const company = s.contacts.filter(isCompanyContact).sort(byCompanyRole);
  if (!s.contacts.length) return <Empty>No contacts for this site or its company.</Empty>;
  return (
    <>
      {own.length ? (
        <div className="contacts">
          {own.map((c) => (
            <ContactCard key={c.id} c={c} siteId={s.id} />
          ))}
        </div>
      ) : (
        <div className="note">No contacts are assigned to this site itself.</div>
      )}
      {company.length > 0 && (
        <details className="company-contacts" open={own.length === 0}>
          <summary>
            Company contacts ({company.length}) <span className="note">serve every site of the company</span>
          </summary>
          <div className="contacts">
            {company.map((c) => (
              <ContactCard key={c.id} c={c} siteId={s.id} />
            ))}
          </div>
        </details>
      )}
    </>
  );
}

function MapsTab({ s }: { s: SiteDetail }) {
  const maps = s.sitemaps.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const [sel, setSel] = useState(0);
  const m = maps[Math.min(sel, maps.length - 1)];
  if (!m) return <Empty>No site maps for this site.</Empty>;
  return (
    <>
      {maps.length > 1 && (
        <div className="seg wrap" role="tablist" aria-label="Site maps">
          {maps.map((x, i) => (
            <button key={x.id} type="button" role="tab" aria-selected={x.id === m.id} className={x.id === m.id ? 'on' : ''} onClick={() => setSel(i)}>
              {x.name}
            </button>
          ))}
        </div>
      )}
      <div className="row-wrap">
        <h3 className="h-sm">{m.name}</h3>
        {m.revisionLabel && <span className="pill">{m.revisionLabel}</span>}
        <SourceBadge source={m.source} />
        <span className="note mono">v{m.version}</span>
        <span className="note">
          <Icon name="lock" /> From the ERP
        </span>
      </div>
      <SitemapView key={m.id} map={m} cameras={s.cameras} devices={s.devices} />
    </>
  );
}

function IngestTab({ s }: { s: SiteDetail }) {
  const { isAdmin } = useSession();
  return (
    <>
      {s.alarmEndpoints && (
        <>
          <div className="lbl">Site endpoints (one device, many cameras)</div>
          <div className="note">For a device that sends alarms for several cameras to one recipient. Each camera's own endpoints are on the Cameras tab.</div>
          <Endpoints e={s.alarmEndpoints} last={s.lastSiteEndpointAlarmAt ?? null} />
          <div className="lbl">Configured ingest addresses</div>
        </>
      )}
      <div className="note">
        Alarm emails sent to these addresses are mapped to this site. {isAdmin && <a href={href.ingest()}>Manage on the Alarm ingest screen.</a>}
      </div>
      {s.ingestAddresses.length === 0 ? (
        <Empty>No ingest address yet.{isAdmin ? ' Create one on the Alarm ingest screen.' : ''}</Empty>
      ) : (
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th scope="col">Address</th>
                <th scope="col">Active</th>
                <th scope="col">Last alarm received (site)</th>
                <th scope="col">Last test received (site)</th>
              </tr>
            </thead>
            <tbody>
              {s.ingestAddresses.map((a) => (
                <tr key={a.id}>
                  <td className="mono">{a.address}</td>
                  <td>{a.active ? <Pill tone="ok">active</Pill> : <Pill>inactive</Pill>}</td>
                  <td className="mono" title={a.lastReceivedAt ? fmtDateTime(a.lastReceivedAt, OPERATOR_TZ) : undefined}>
                    {a.lastReceivedAt ? `${fmtDateTime(a.lastReceivedAt, s.timeZone)} · ${relAge(a.lastReceivedAt)}` : 'Never'}
                  </td>
                  <td className="mono" title={a.testReceivedAt ? fmtDateTime(a.testReceivedAt, OPERATOR_TZ) : undefined}>
                    {a.testReceivedAt ? `${fmtDateTime(a.testReceivedAt, s.timeZone)} · ${relAge(a.testReceivedAt)}` : 'Never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ---------- screen ---------- */

export function SiteDetailScreen({ id, tab }: { id: string; tab: string | null }) {
  const load = useLoad((signal) => api.site(id, signal), [id]);
  useRefreshOn(['replica'], load.reload, 1000);
  useStreamEvent('health', (h) => {
    load.setData((cur) =>
      cur && cur.cameras.some((c) => c.id === h.cameraId)
        ? {
            ...cur,
            cameras: cur.cameras.map((c) =>
              c.id === h.cameraId ? { ...c, health: { status: h.status, changedAt: h.changedAt, lastSeenAt: h.status === 'online' ? h.changedAt : (c.health?.lastSeenAt ?? null) } } : c,
            ),
          }
        : cur,
    );
  });
  const current: Tab = (TABS as readonly string[]).includes(tab ?? '') ? (tab as Tab) : 'overview';
  const all = load.data;
  // Records the ERP deleted stay in the replica as history (contract 6.5) but are not part of the site any more:
  // the tabs, counts and site maps show the live ones, and each tab lists the deleted ones apart, collapsed.
  const s = all ? { ...all, cameras: all.cameras.filter((c) => c.active), devices: all.devices.filter((d) => d.active), contacts: all.contacts.filter((c) => c.active) } : all;
  const deleted = all ? { cameras: all.cameras.filter((c) => !c.active), devices: all.devices.filter((d) => !d.active), contacts: all.contacts.filter((c) => !c.active) } : null;

  if (load.error && !s) {
    return (
      <div className="page">
        <a href={href.sites()} className="back">
          <Icon name="back" />
          Sites
        </a>
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the site" />
      </div>
    );
  }
  if (!s) {
    return (
      <div className="page">
        <Loading label="Loading site…" />
      </div>
    );
  }

  const counts: Record<Tab, number | null> = {
    overview: null,
    cameras: s.cameras.length,
    devices: s.devices.length,
    contacts: s.contacts.length,
    maps: s.sitemaps.length,
    ingest: s.ingestAddresses.length,
  };
  const labels: Record<Tab, string> = {
    overview: 'Overview',
    cameras: 'Cameras',
    devices: 'Devices',
    contacts: 'Contacts',
    maps: 'Site maps',
    ingest: 'Ingest addresses',
  };

  return (
    <div className="page">
      <a href={href.sites(s.companyId)} className="back">
        <Icon name="back" />
        {s.companyName} sites
      </a>
      <div className="page-h">
        <div>
          <h2>
            {s.name} <StatusPill status={s.status} /> <SourceBadge source={s.source} />
            {s.held && <Pill tone="warn">held change</Pill>}
          </h2>
          <p>
            {s.companyName} · <span className="mono">{s.siteCode ?? s.id}</span> · {s.timeZone}
          </p>
        </div>
        <span className="sp" />
        <ErpLink url={s.erpUrl} />
      </div>
      <div className="stabs" role="tablist" aria-label="Site sections">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={current === t}
            className={current === t ? 'on' : ''}
            onClick={() => navigate(href.site(s.id, t === 'overview' ? undefined : t), { replace: true })}
          >
            {labels[t]}
            {counts[t] !== null && <span className="n">{counts[t]}</span>}
          </button>
        ))}
      </div>
      {load.error ? <ErrorBox error={load.error} onRetry={load.reload} title="Refresh failed; showing earlier data" /> : null}
      <div className="tabbody" role="tabpanel" aria-label={labels[current]}>
        {current === 'overview' && <Overview s={s} />}
        {current === 'cameras' && (
          <>
            <CamerasTab s={s} />
            <DeletedInErp what="cameras" items={deleted!.cameras.map((c) => ({ id: c.id, name: c.name, note: c.nxCameraId ? `NX ${c.nxCameraId}` : null }))} />
          </>
        )}
        {current === 'devices' && (
          <>
            <DevicesTab s={s} />
            <DeletedInErp what="devices" items={deleted!.devices.map((d) => ({ id: d.id, name: d.name, note: d.kind.replace(/_/g, ' ') }))} />
          </>
        )}
        {current === 'contacts' && (
          <>
            <ContactsTab s={s} />
            <DeletedInErp what="contacts" items={deleted!.contacts.map((c) => ({ id: c.id, name: c.displayName, note: c.jobTitle }))} />
          </>
        )}
        {current === 'maps' && <MapsTab s={s} />}
        {current === 'ingest' && <IngestTab s={s} />}
      </div>
    </div>
  );
}
